import isEqual from 'lodash/isEqual';
import { computed, makeObservable, override } from 'mobx';
import flatten from 'lodash/flatten';
import compact from 'lodash/compact';

import type { TableCellParams } from 'chaika-frontend-types/frontend-cms';
import { TableFieldCellType } from 'chaika-enums';

import { TableData } from '@components/Fields/Table/types';
import { Validation } from '@utils/Validation';

import { AbstractField } from './AbstractField';

const enum CellNamePrefix {
    MinValue = 'min_for_',
    MaxValue = 'max_for_',
    TextValue = 'text_for_',
    IsOutOfRange = 'out_of_range_',
}

export class TableField extends AbstractField<TableData, TableData> {
    public static getValueCellNameFromRefValueName(name: string): string {
        return name.replace(CellNamePrefix.MinValue, '').replace(CellNamePrefix.MaxValue, '');
    }

    public static makeCalculateKey(filedName: string, cellName: string): string {
        return `${filedName}__${cellName}`;
    }

    public static getCalculatedFiledCells(cells: TableCellParams[][]): TableCellParams[] {
        return flatten(cells).filter(({ type }) => type === TableFieldCellType.Calculate);
    }

    public static getDictionaryNames(cells: TableCellParams[][]): string[] {
        return compact(flatten(cells).map(({ dictionaryName }) => dictionaryName));
    }

    public static makeRefValueNameFromValueCellName(name: string, type: TableFieldCellType): string {
        return `${type === TableFieldCellType.MaxValue ? CellNamePrefix.MaxValue : CellNamePrefix.MinValue}${name}`;
    }

    public static isRefValueCell(name: string): boolean {
        return (
            name.startsWith(CellNamePrefix.MaxValue) ||
            name.startsWith(CellNamePrefix.MinValue) ||
            name.startsWith(CellNamePrefix.TextValue) ||
            name.startsWith(CellNamePrefix.IsOutOfRange)
        );
    }

    public static isMaxRefCell(name: string): boolean {
        return name.startsWith(CellNamePrefix.MaxValue);
    }

    public static isOutOfRangeCell(name: string): boolean {
        return name.startsWith(CellNamePrefix.IsOutOfRange);
    }

    protected defaultNullValue = {};

    constructor(params: TableData, name: string) {
        super(params, name);
        makeObservable(this);
    }

    @override
    public get hasData(): boolean {
        const valuesKeys = Object.keys(this.fieldValue).filter(
            cellName =>
                !cellName.startsWith('unit_') &&
                !cellName.startsWith('parameter_') &&
                !TableField.isRefValueCell(cellName),
        );
        return valuesKeys.length > 0 && valuesKeys.some(key => this.fieldValue[key].length > 0);
    }

    @computed
    public get hasRefValueCells(): boolean {
        return Object.keys(this.fieldValue).some(cellName => TableField.isRefValueCell(cellName));
    }

    public isRowValid(row: TableCellParams[], refCellDefaultValues: TableData): boolean {
        return this.hasRefValueCells ? this.checkRow(row, refCellDefaultValues) : true;
    }

    public isEqual(value: TableData): boolean {
        return isEqual(value, this.fieldValue);
    }

    public getPreview(): string {
        return Object.values(this.fieldValue).join(', ');
    }

    public makeUpdateParams(): TableData {
        return this.fieldValue;
    }

    protected setDefaultValueValue(): void {
        this.defaultNullValue = {};
    }

    protected setFieldValue(value: TableData): void {
        this.fieldValue = value;
    }

    private checkRow(row: TableCellParams[], refCellDefaultValues: TableData): boolean {
        const refFieldNames = row.map(({ name }) => name).filter(name => TableField.isRefValueCell(name));

        return refFieldNames.length > 0
            ? refFieldNames.every(name => this.checkRefValue(name, refCellDefaultValues))
            : true;
    }

    private checkRefValue(refValueName: string, refCellDefaultValues: TableData): boolean {
        const refValue = this.fieldValue[refValueName] ?? refCellDefaultValues[refValueName];
        const value = this.fieldValue[TableField.getValueCellNameFromRefValueName(refValueName)];
        const isMaxValue = refValueName.startsWith(CellNamePrefix.MaxValue);

        return Validation.isNumberString(value) && Validation.isNumberString(refValue)
            ? isMaxValue
                ? Number(value) < Number(refValue)
                : Number(value) > Number(refValue)
            : true;
    }
}
